import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import {
  IsNotEmpty,
  IsOptional,
  IsString,
  IsNumber,
  IsUUID,
  IsDateString,
  IsEnum,
  ValidateNested,
} from 'class-validator'
import {
  PedagogicContentState,
  ContentVisibility,
} from '../../../enums/content.enum'
import { LessonCapabilities } from '@sphere/shared/domain'
import { Type } from 'class-transformer'
import { ChapterDto } from '../../chapters/response/chapter.dto'
import { SubjectDto } from '../../subjects/response/subject.dto'
import { LevelDto } from '../../levels/response/level.dto'
import { LessonStatusDto } from '../../lesson-status'
import { LessonTypeDto } from '../../lesson-type'
import { LessonTeachingResponseDto } from '../../lesson-teachings/response/lesson-teaching-response.dto'
import { ConceptDto } from '@sphere/curriculum/domain'

export class LessonDto {
  @ApiProperty({
    description: 'ID de la leçon',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty()
  @IsUUID('4')
  id!: string

  @ApiProperty({
    description: 'Titre de la leçon',
    example: 'Leçon 1 - Introduction',
    type: 'string',
  })
  @IsNotEmpty()
  @IsString()
  title!: string

  @ApiPropertyOptional({
    description: 'Description de la leçon',
    type: 'string',
  })
  @IsOptional()
  @IsString()
  description?: string

  @ApiPropertyOptional({
    description: 'ID du type',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  typeId?: string

  @ApiPropertyOptional({
    description: 'Durée en minutes',
    type: Number,
  })
  @IsOptional()
  @IsNumber()
  duration?: number

  @ApiPropertyOptional({
    description: 'ID du chapitre',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  chapterId?: string

  @ApiPropertyOptional({
    description: 'Ordre dans le chapitre',
    type: Number,
  })
  @IsOptional()
  @IsNumber()
  orderInChapter?: number

  @ApiPropertyOptional({
    description: 'État de la leçon',
    enum: PedagogicContentState,
  })
  @IsOptional()
  @IsEnum(PedagogicContentState)
  state?: PedagogicContentState

  @ApiPropertyOptional({
    description: 'ID du statut',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: () => LessonStatusDto,
  })
  @IsOptional()
  @IsUUID('4')
  status?: LessonStatusDto

  @ApiPropertyOptional({
    description: 'Type de leçon',
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsOptional()
  @ValidateNested()
  @Type(() => LessonTypeDto)
  type?: LessonTypeDto

  @ApiPropertyOptional({
    type: 'string',
    description: "ID de l'établissement",
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsOptional()
  @IsUUID('4')
  establishmentId?: string

  @ApiPropertyOptional({
    description: 'ID de la matière externe',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  subjectExternalId?: string

  @ApiPropertyOptional({
    description: 'ID du niveau externe',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  levelExternalId?: string

  @ApiPropertyOptional({
    description: "ID de l'utilisateur propriétaire",
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  ownerUserId?: string

  @ApiPropertyOptional({
    description: "ID de l'utilisateur créateur",
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  createdByUserId?: string

  @ApiPropertyOptional({
    description: 'Visibilité de la leçon',
    enum: ContentVisibility,
  })
  @IsOptional()
  @IsEnum(ContentVisibility)
  visibility?: ContentVisibility

  @ApiPropertyOptional({
    description: 'ID du clone source',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  clonedFromId?: string

  @ApiPropertyOptional({
    description: 'Nombre de ressources',
    type: Number,
  })
  @IsOptional()
  @IsNumber()
  resources_count?: number



  @ApiPropertyOptional({type: () => ChapterDto, description: 'Chapitre associé' })
  @IsOptional()
  @ValidateNested()
  @Type(() => ChapterDto)
  chapter?: ChapterDto

  @ApiPropertyOptional({
    description: 'IDs des ProgramConcepts associés à la leçon',
    type: [String],
  })
  @IsOptional()
  @IsUUID('4', { each: true })
  programConceptIds?: string[]

  @ApiPropertyOptional({ description: 'Matière associée' })
  @IsOptional()
  @ValidateNested()
  @Type(() => SubjectDto)
  subject?: SubjectDto

  @ApiPropertyOptional({ description: 'Niveau associé' })
  @IsOptional()
  @ValidateNested()
  @Type(() => LevelDto)
  level?: LevelDto

  @ApiPropertyOptional({ description: 'Enseignements associés', type: () => [LessonTeachingResponseDto] })
  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => LessonTeachingResponseDto)
  teachings?: LessonTeachingResponseDto[]

  @ApiPropertyOptional({ description: 'Concepts associés', type: () => [ConceptDto] })
  @IsOptional()
  concepts?: ConceptDto[]

  @ApiProperty({
    description: 'Date de création',
  })
  @IsNotEmpty()
  @IsDateString()
  createdAt!: Date

  @ApiProperty({
    description: 'Date de mise à jour',
  })
  @IsNotEmpty()
  @IsDateString()
  updatedAt!: Date

  @ApiPropertyOptional({
    description: 'Date de suppression',
  })
  @IsOptional()
  @IsDateString()
  deletedAt?: Date

  @ApiPropertyOptional({
    description: "ID de l'utilisateur qui a supprimé",
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  deletedByUserId?: string

  @ApiPropertyOptional({ description: 'Capacités de la leçon', type: Object, additionalProperties: true })
  @IsOptional()
  capabilities?: LessonCapabilities
}
